import { useState, useEffect, useCallback } from "react";
import type { ScheduledGroup } from "@/hooks/useAdminAuth";
import { formatDateTime } from "@/pages/admin/system/constants";

interface ScheduledPanelProps {
  fetchScheduledNotifications: () => Promise<any>;
  cancelScheduledNotification: (payload: { batch_id?: string; notification_ids?: number[] }) => Promise<any>;
  sendScheduledNow: (payload: { batch_id?: string; notification_ids?: number[] }) => Promise<any>;
  updateScheduledNotification: (payload: { batch_id?: string; notification_ids?: number[]; scheduled_at: string }) => Promise<any>;
  isGlobalView: () => boolean;
  onMessage: (msg: string) => void;
}

export default function ScheduledPanel({
  fetchScheduledNotifications,
  cancelScheduledNotification,
  sendScheduledNow,
  updateScheduledNotification,
  isGlobalView,
  onMessage,
}: ScheduledPanelProps) {
  const [scheduledGroups, setScheduledGroups] = useState<ScheduledGroup[]>([]);
  const [schedLoading, setSchedLoading] = useState(false);
  const [editScheduleTarget, setEditScheduleTarget] = useState<ScheduledGroup | null>(null);
  const [editScheduleTime, setEditScheduleTime] = useState("");
  const [editScheduleLoading, setEditScheduleLoading] = useState(false);
  const [sendNowTarget, setSendNowTarget] = useState<ScheduledGroup | null>(null);
  const [sendNowLoading, setSendNowLoading] = useState(false);
  const [cancelSchedTarget, setCancelSchedTarget] = useState<ScheduledGroup | null>(null);
  const [cancelSchedLoading, setCancelSchedLoading] = useState(false);

  const loadScheduled = useCallback(async () => {
    setSchedLoading(true);
    const res = await fetchScheduledNotifications();
    setSchedLoading(false);
    if (res?.success) setScheduledGroups(res.grouped || []);
  }, [fetchScheduledNotifications]);

  useEffect(() => { loadScheduled(); }, [loadScheduled]);

  const confirmCancelScheduled = async () => {
    if (!cancelSchedTarget) return;
    setCancelSchedLoading(true);
    const group = cancelSchedTarget;
    const res = await cancelScheduledNotification(group.batch_id ? { batch_id: group.batch_id } : { notification_ids: group.ids });
    setCancelSchedLoading(false);
    if (res?.success) { setCancelSchedTarget(null); loadScheduled(); onMessage("定时通知已取消"); }
    else { setCancelSchedTarget(null); onMessage(res?.error || "取消失败"); }
  };

  const confirmSendNow = async () => {
    if (!sendNowTarget) return;
    setSendNowLoading(true);
    const group = sendNowTarget;
    const nowIso = new Date().toISOString();
    const timePayload: any = { scheduled_at: nowIso };
    if (group.batch_id) timePayload.batch_id = group.batch_id;
    else timePayload.notification_ids = group.ids;
    await updateScheduledNotification(timePayload);
    const res = await sendScheduledNow(group.batch_id ? { batch_id: group.batch_id } : { notification_ids: group.ids });
    setSendNowLoading(false);
    if (res?.success) { setSendNowTarget(null); loadScheduled(); onMessage("已立即发送！"); }
    else { setSendNowTarget(null); onMessage(res?.error || "发送失败"); }
  };

  const openEditSchedule = (group: ScheduledGroup) => {
    const d = new Date(group.scheduled_at);
    const localStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    setEditScheduleTime(localStr);
    setEditScheduleTarget(group);
  };

  const handleSaveSchedule = async () => {
    if (!editScheduleTarget || !editScheduleTime) return;
    if (new Date(editScheduleTime) <= new Date()) { onMessage("修改发送时间必须晚于当前操作时间"); return; }
    setEditScheduleLoading(true);
    const [datePart, timePart] = editScheduleTime.split("T");
    const [y, m, d] = datePart.split("-").map(Number);
    const [hh, mm] = timePart.split(":").map(Number);
    const scheduledIso = new Date(y, m - 1, d, hh, mm).toISOString();
    const payload: any = { scheduled_at: scheduledIso };
    if (editScheduleTarget.batch_id) payload.batch_id = editScheduleTarget.batch_id;
    else payload.notification_ids = editScheduleTarget.ids;
    const res = await updateScheduledNotification(payload);
    setEditScheduleLoading(false);
    if (res?.success) { setEditScheduleTarget(null); loadScheduled(); onMessage("发送时间已更新"); }
    else onMessage(res?.error || "更新失败");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[#1a3a5c]">定时发送任务</h3>
          <p className="text-[11px] text-gray-400 mt-0.5">查看和管理待发送的定时通知</p>
        </div>
        <button onClick={loadScheduled} className="px-3 py-2 bg-gray-50 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
          <i className="ri-refresh-line"></i>刷新
        </button>
      </div>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {schedLoading ? (
          <div className="p-8 text-center"><div className="w-8 h-8 border-2 border-[#1a3a5c] border-t-transparent rounded-full animate-spin mx-auto"></div></div>
        ) : scheduledGroups.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            <i className="ri-time-line text-2xl mb-2 block text-gray-300"></i>
            暂无待发送的定时通知
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {scheduledGroups.map((group, i) => (
              <div key={group.batch_id || i} className="px-5 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="text-sm font-medium text-[#1a3a5c] truncate">{group.title}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 ${group.type === "success" ? "bg-emerald-50 text-emerald-600" : group.type === "warning" ? "bg-amber-50 text-amber-600" : group.type === "error" ? "bg-red-50 text-red-600" : "bg-[#1a3a5c]/5 text-[#1a3a5c]"}`}>
                        {group.type === "success" ? "好消息" : group.type === "warning" ? "提醒" : group.type === "error" ? "紧急" : "普通"}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 shrink-0">{group.client_count} 位客户</span>
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-1 mb-2">{group.content}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-[11px] text-[#c9a96e] flex items-center gap-1">
                        <i className="ri-time-line"></i>发送时间：{formatDateTime(group.scheduled_at)}
                      </p>
                      {group.sender_name && isGlobalView() && (
                        <span className="text-[10px] text-gray-400 flex items-center gap-1">
                          <i className="ri-user-line"></i>{group.sender_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => openEditSchedule(group)} className="px-2.5 py-1.5 bg-[#c9a96e]/10 text-[#c9a96e] rounded-lg text-[11px] font-medium hover:bg-[#c9a96e]/20 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-edit-line mr-0.5"></i>改时</button>
                    <button onClick={() => setSendNowTarget(group)} className="px-2.5 py-1.5 bg-emerald-50 text-emerald-600 rounded-lg text-[11px] font-medium hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-send-plane-line mr-0.5"></i>立即发</button>
                    <button onClick={() => setCancelSchedTarget(group)} className="px-2.5 py-1.5 bg-red-50 text-red-500 rounded-lg text-[11px] font-medium hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-delete-bin-line mr-0.5"></i>取消</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editScheduleTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-[#1a3a5c]">修改发送时间</h3>
              <button onClick={() => setEditScheduleTarget(null)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <p className="text-xs text-gray-500 mb-4">修改定时通知「{editScheduleTarget.title}」的发送时间，共 {editScheduleTarget.client_count} 位客户</p>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">发送时间</label>
            <input type="datetime-local" value={editScheduleTime} onChange={(e) => setEditScheduleTime(e.target.value)} min={new Date().toISOString().slice(0, 16)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer mb-5" />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setEditScheduleTarget(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleSaveSchedule} disabled={editScheduleLoading || !editScheduleTime} className="px-5 py-2 bg-[#c9a96e] text-white rounded-lg text-sm font-medium hover:bg-[#b8944f] transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                {editScheduleLoading ? <><i className="ri-loader-4-line animate-spin mr-1"></i>保存中...</> : "确认修改"}
              </button>
            </div>
          </div>
        </div>
      )}

      {sendNowTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-send-plane-line text-emerald-500 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-[#1a3a5c] mb-1">确认立即发送？</h3>
            <p className="text-xs text-gray-400 mb-5">
              定时通知「<span className="font-medium text-[#1a3a5c]">{sendNowTarget.title}</span>」将立即推送给 {sendNowTarget.client_count} 位客户。
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setSendNowTarget(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={confirmSendNow} disabled={sendNowLoading} className="px-5 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                {sendNowLoading ? <><i className="ri-loader-4-line animate-spin mr-1"></i>发送中...</> : "确认发送"}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelSchedTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-error-warning-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-[#1a3a5c] mb-1">确认取消定时通知？</h3>
            <p className="text-xs text-gray-400 mb-5">
              此操作将永久取消定时通知「<span className="font-medium text-[#1a3a5c]">{cancelSchedTarget.title}</span>」，共 {cancelSchedTarget.client_count} 位客户将不再收到此通知。
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setCancelSchedTarget(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={confirmCancelScheduled} disabled={cancelSchedLoading} className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                {cancelSchedLoading ? <><i className="ri-loader-4-line animate-spin mr-1"></i>取消中...</> : "确认取消"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}